import React, { createContext, useEffect, ReactNode } from 'react';
import { Socket } from 'socket.io-client';
import useLocalizedNavigate from 'hooks/useLocalizedNavigate';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import { socketService } from 'services/socketService';
import {
  updateSocketBalance,
  logoutUser,
  incrementUnreadNotificationCount,
  fetchMyLevel,
  fetchCurrentUser,
} from 'store/authSlice';
import { fetchLevels } from 'store/levelSlice';
import { showToast, ToastType } from 'store/toastSlice';
import { updateBonusLocal, reorderBonusesLocal } from 'store/bonusesApi';
import { updateSiteWheelPrizes } from 'store/siteSlice';

interface SocketContextType {
  socket: Socket | null;
}

export const SocketContext = createContext<SocketContextType>({ socket: null });

interface SocketProviderProps {
  children: ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const navigate = useLocalizedNavigate();
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const [socket, setSocket] = React.useState<Socket | null>(socketService.getSocket());

  useEffect(() => {
    const initSocket = async () => {
      if (isAuthenticated) {
        const s = await socketService.connect();
        setSocket(s);
      } else {
        socketService.disconnect();
        setSocket(null);
      }
    };

    initSocket();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!socket) return;

    const handleConnect = () => {
      // Socket delivery is best-effort: refetch balance/user when reconnected
      dispatch(fetchCurrentUser(true) as any);
    };

    const handleBalanceUpdate = (data: any) => {
      dispatch(updateSocketBalance(data));

      let message = 'Balance updated!';
      let type: ToastType = 'info';

      if (data.reason === 'deposit') {
        message = 'Deposit credited';
        type = 'success';
      } else if (data.reason === 'withdraw') {
        message = 'Your balance decreased!';
      } else if (data.reason === 'refund') {
        message = 'Your balance has been refunded!';
        type = 'success';
      }

      dispatch(
        showToast({
          text: message,
          type,
          position: window.innerWidth <= 1024 ? 'top' : 'left',
        }),
      );
    };

    const handleDepositStatusChanged = (data: any) => {
      if (!data) return;

      if (data.status === 'processing') {
        const cryptoInfo =
          data.cryptoAmount && data.cryptoCurrency
            ? ` (${data.cryptoAmount} ${data.cryptoCurrency})`
            : data.amount && data.currency
              ? ` (${data.amount} ${data.currency})`
              : '';

        dispatch(
          showToast({
            text: `Deposit detected, confirming...${cryptoInfo}`,
            type: 'info',
            position: window.innerWidth <= 1024 ? 'top' : 'left',
          }),
        );
      }
    };

    const handleWithdrawStatusChanged = (data: any) => {
      if (!data || !data.status) return;

      const amountInfo =
        data.amount && data.currency
          ? ` (${data.amount} ${data.currency})`
          : data.amount
            ? ` (${data.amount})`
            : '';

      let text = '';
      let type: ToastType = 'info';

      switch (data.status) {
        case 'processing':
          text = `Withdrawal${amountInfo} is processing`;
          type = 'info';
          break;
        case 'completed':
          text = `Withdrawal${amountInfo} completed`;
          type = 'success';
          break;
        case 'failed':
          text = `Withdrawal${amountInfo} failed${data.failReason ? `: ${data.failReason}` : ''}. Funds refunded.`;
          type = 'error';
          break;
        case 'cancelled':
          text = `Withdrawal${amountInfo} cancelled. Funds refunded.`;
          type = 'info';
          break;
        default:
          return;
      }

      dispatch(
        showToast({
          text,
          type,
          position: window.innerWidth <= 1024 ? 'top' : 'left',
        }),
      );
    };

    const handleSessionRevoked = (data: any) => {
      if (data && data.reason === 'logged_in_elsewhere') {
        dispatch(
          showToast({
            text: 'You have been logged out because your session was opened on another device.',
            position: window.innerWidth <= 1024 ? 'top' : 'left',
            type: 'error',
          }),
        );
        dispatch(logoutUser());
        navigate('/');
      }
    };

    const handleBonusDefinitionUpdated = (data: any) => {
      dispatch(updateBonusLocal(data));
    };

    const handleBonusDefinitionReordered = (data: any) => {
      dispatch(reorderBonusesLocal(data));
    };

    const handleNotificationCreated = (data: any) => {
      dispatch(incrementUnreadNotificationCount());
    };

    const handleAccountBlocked = (data: any) => {
      dispatch(
        showToast({
          text: 'Your account has been blocked.',
          position: window.innerWidth <= 1024 ? 'top' : 'left',
          type: 'error',
        }),
      );
      dispatch(logoutUser());
      navigate('/');
    };

    const handleLevelsConfigUpdated = () => {
      dispatch(fetchMyLevel({ silent: true }) as any);
      dispatch(fetchLevels({ silent: true }) as any);
    };

    const handleSiteWheelPrizesUpdated = (data: any) => {
      if (data) {
        dispatch(updateSiteWheelPrizes(data));
      }
    };

    socket.on('connect', handleConnect);
    socket.on('deposit.statusChanged', handleDepositStatusChanged);
    socket.on('withdraw.statusChanged', handleWithdrawStatusChanged);
    socket.on('balance.updated', handleBalanceUpdate);
    socket.on('session.revoked', handleSessionRevoked);
    socket.on('bonus-definition.updated', handleBonusDefinitionUpdated);
    socket.on('bonus-definition.reordered', handleBonusDefinitionReordered);
    socket.on('notification.created', handleNotificationCreated);
    socket.on('account.blocked', handleAccountBlocked);
    socket.on('levels_config_update', handleLevelsConfigUpdated);
    socket.on('site-wheel-prizes.updated', handleSiteWheelPrizesUpdated);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('deposit.statusChanged', handleDepositStatusChanged);
      socket.off('withdraw.statusChanged', handleWithdrawStatusChanged);
      socket.off('balance.updated', handleBalanceUpdate);
      socket.off('session.revoked', handleSessionRevoked);
      socket.off('bonus-definition.updated', handleBonusDefinitionUpdated);
      socket.off('bonus-definition.reordered', handleBonusDefinitionReordered);
      socket.off('notification.created', handleNotificationCreated);
      socket.off('account.blocked', handleAccountBlocked);
      socket.off('levels_config_update', handleLevelsConfigUpdated);
      socket.off('site-wheel-prizes.updated', handleSiteWheelPrizesUpdated);
    };
  }, [socket, dispatch, navigate]);

  return <SocketContext.Provider value={{ socket }}>{children}</SocketContext.Provider>;
};
